import { useState } from 'react';
import { api, ApiError, newId } from '../api/client';
import type { ImportPreview, ImportResolution } from '../api/types';
import { CategoryPicker } from '../components/Modal';
import { childCategories, paymentMethods, topCategories } from '../lib/catalog';
import { krw } from '../lib/format';
import { useApp } from '../state';

/**
 * 내보내기(GET /export)와 시제품 데이터 가져오기(설계서 10장).
 * 가져오기: 원본 붙여넣기 → 미리보기(저장 안 함) → 모르는 분류·수단 결정 → 합계 확인 → 한 번에 확정.
 */
export function DataPage() {
  return (
    <section>
      <article className="card">
        <div className="card-head">
          <div>
            <h2>전체 데이터 내보내기</h2>
            <p className="muted">분류·수단·계좌·거래(삭제 기록 포함)·반복 항목·예산을 JSON 한 파일로 받습니다. 인증 정보는 들어가지 않습니다.</p>
          </div>
          <a className="button primary" href="/api/v1/export" download>
            JSON 내려받기
          </a>
        </div>
      </article>
      <ImportPanel />
    </section>
  );
}

function parseSource(text: string): unknown {
  let value: unknown = JSON.parse(text);
  if (typeof value === 'string') value = JSON.parse(value);
  return value;
}

function ImportPanel() {
  const { catalog, reloadCatalog, changed, toast } = useApp();
  const [source, setSource] = useState('');
  const [includeSamples, setIncludeSamples] = useState(false);
  const [includeSeedRecurring, setIncludeSeedRecurring] = useState(false);
  const [resolutions, setResolutions] = useState<Record<string, ImportResolution>>({});
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [stale, setStale] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ importedTransactions: number; incomeKrw: string; expenseKrw: string; importedRecurringRules: number } | null>(null);
  const [confirmKey, setConfirmKey] = useState(newId);

  async function runPreview(nextResolutions = resolutions) {
    setError(null);
    setResult(null);
    let data: unknown;
    try {
      data = parseSource(source);
    } catch {
      return setError('JSON 형식이 아닙니다. 시제품에서 복사한 내용을 그대로 붙여넣어 주세요.');
    }
    setBusy(true);
    try {
      const res = await api.post<{ data: ImportPreview }>('/imports/preview', { format: 'chagok-prototype-v1', data, includeSamples, includeSeedRecurring, resolutions: nextResolutions });
      setPreview(res.data);
      setStale(false);
      setConfirmKey(newId());
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message}${Object.keys(e.fields).length ? ` (${Object.entries(e.fields).map(([k, v]) => `${k}: ${v}`).join(', ')})` : ''}` : '미리보기에 실패했습니다.');
    } finally {
      setBusy(false);
    }
  }

  function decide(key: string, resolution: ImportResolution | null) {
    const next = { ...resolutions };
    if (resolution) next[key] = resolution;
    else delete next[key];
    setResolutions(next);
    setStale(true);
  }

  async function confirm() {
    if (!preview) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ data: NonNullable<typeof result> }>(`/imports/${preview.id}/confirm`, {}, { idempotencyKey: confirmKey });
      setResult(res.data);
      setPreview(null);
      setResolutions({});
      await reloadCatalog();
      changed();
      toast('가져오기를 확정했습니다.');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '확정하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }

  const s = preview?.summary;
  const pending = preview ? preview.categoryGroups.filter((g) => !g.match || resolutions[g.key]) : [];
  const pendingPayments = preview ? preview.paymentGroups.filter((g) => !g.match || resolutions[g.key]) : [];

  return (
    <article className="card spaced">
      <h2>시제품 데이터 가져오기</h2>
      <ol className="steps">
        <li>
          예전 시제품 사이트를 연 브라우저에서 개발자 도구 콘솔을 열고 <code>copy(localStorage.getItem('chagok-v1'))</code>를 실행합니다. (시제품 데이터는 그 사이트에만 저장되어 있어 여기서 자동으로 읽을 수 없습니다.)
        </li>
        <li>복사된 내용을 아래에 붙여넣거나 JSON 파일을 선택합니다.</li>
        <li>미리보기에서 합계와 연결할 분류를 확인한 뒤 확정합니다. 확정 전에는 아무것도 저장하지 않습니다.</li>
      </ol>
      <textarea rows={6} value={source} onChange={(e) => (setSource(e.target.value), setStale(true))} placeholder='{"rows":[...],"subs":[...],"catalog":{...}}' aria-label="시제품 JSON" spellCheck={false} />
      <div className="inline-form">
        <input
          type="file"
          accept="application/json,.json"
          aria-label="JSON 파일 선택"
          onChange={async (e) => {
            const file = e.target.files?.[0];
            if (file) {
              setSource(await file.text());
              setStale(true);
            }
          }}
        />
      </div>
      <label className="check">
        <input type="checkbox" checked={includeSamples} onChange={(e) => (setIncludeSamples(e.target.checked), setStale(true))} /> 시제품 예시 데이터(sample-*)도 포함 — 예시를 실제 기록처럼 써 왔을 때만
      </label>
      <label className="check">
        <input type="checkbox" checked={includeSeedRecurring} onChange={(e) => (setIncludeSeedRecurring(e.target.checked), setStale(true))} /> 시제품 기본 정기결제(넷플릭스·음악 서비스 예시)도 포함
      </label>
      <button className="primary save" disabled={busy || !source.trim()} onClick={() => void runPreview()}>
        {busy ? '확인 중…' : preview ? '결정 반영해서 다시 미리보기' : '미리보기'}
      </button>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {result && (
        <p className="notice" role="status">
          가져오기 완료: 거래 {result.importedTransactions}건 (수입 {krw(result.incomeKrw)}, 지출 {krw(result.expenseKrw)}), 반복 항목 {result.importedRecurringRules}건. 가져온 반복 항목은 반복 항목 화면에서 분류를 정해주세요.
        </p>
      )}

      {preview && s && (
        <div className="import-preview">
          <div className="metrics three">
            <article className="metric">
              <span>가져올 거래</span>
              <strong>{s.transactionCount}건</strong>
              <small>반복 항목 {s.recurringCount}건</small>
            </article>
            <article className="metric">
              <span>수입 합계</span>
              <strong>{krw(s.incomeKrw)}</strong>
            </article>
            <article className="metric">
              <span>지출 합계</span>
              <strong>{krw(s.expenseKrw)}</strong>
            </article>
          </div>
          <p className="hint">
            제외: 예시 {s.excluded.samples}건 · 기본 정기결제 {s.excluded.seedRecurring}건 · 이미 가져옴 {s.excluded.alreadyImported}건 · 오류 {s.excluded.invalid}건 · 선택해서 제외 {s.excluded.skippedByChoice}건
            {s.warnings.subcategoryDefaulted ? ` · 소분류가 없어 ‘기타’로 둔 기록 ${s.warnings.subcategoryDefaulted}건` : ''}
            {s.warnings.recurringStartDefaulted ? ` · 시작일이 없어 오늘부터 시작하는 반복 항목 ${s.warnings.recurringStartDefaulted}건` : ''}
          </p>

          {pending.length > 0 && <h3>연결할 분류 정하기</h3>}
          {pending.map((g) => (
            <GroupDecision key={g.key} title={`${g.kind === 'income' ? '수입' : '지출'} · ${g.categoryName} › ${g.subcategoryName}`} subtitle={`${g.rowCount}건 · ${krw(g.totalKrw)}${g.parent?.archived ? ' · 대분류가 삭제되어 있어 새로 만들 수 없음' : ''}`}>
              <select
                value={resolutions[g.key]?.action ?? ''}
                onChange={(e) => {
                  const action = e.target.value;
                  decide(g.key, action === 'create' ? { action: 'create' } : action === 'skip' ? { action: 'skip' } : action === 'map' ? { action: 'map', categoryId: '', subcategoryId: '' } : null);
                }}
                aria-label="처리 방법"
              >
                <option value="">{g.match ? '같은 이름의 분류에 연결' : '선택 필요'}</option>
                {g.canCreate && <option value="create">새 분류로 만들기</option>}
                <option value="map">다른 기존 분류에 연결</option>
                <option value="skip">이 기록들은 가져오지 않기</option>
              </select>
              {resolutions[g.key]?.action === 'map' && (
                <CategoryPicker
                  tops={topCategories(catalog, g.kind, true)}
                  subs={childCategories(catalog, (resolutions[g.key] as { categoryId: string }).categoryId, true)}
                  categoryId={(resolutions[g.key] as { categoryId: string }).categoryId}
                  subcategoryId={(resolutions[g.key] as { subcategoryId: string }).subcategoryId}
                  onChange={(categoryId, subcategoryId) => decide(g.key, { action: 'map', categoryId, subcategoryId })}
                />
              )}
            </GroupDecision>
          ))}

          {pendingPayments.length > 0 && <h3>연결할 결제·입금수단 정하기</h3>}
          {pendingPayments.map((g) => (
            <GroupDecision key={g.key} title={`${g.kind === 'income' ? '입금' : '결제'} · ${g.name}`} subtitle={`${g.rowCount}건`}>
              <select
                value={resolutions[g.key]?.action === 'mapPayment' ? `pm:${(resolutions[g.key] as { paymentMethodId: string }).paymentMethodId}` : (resolutions[g.key]?.action ?? '')}
                onChange={(e) => {
                  const v = e.target.value;
                  decide(g.key, v === 'create' ? { action: 'create' } : v === 'skip' ? { action: 'skip' } : v.startsWith('pm:') ? { action: 'mapPayment', paymentMethodId: v.slice(3) } : null);
                }}
                aria-label="처리 방법"
              >
                <option value="">{g.match ? '같은 이름의 수단에 연결' : '선택 필요'}</option>
                {g.canCreate && <option value="create">새 수단으로 만들기</option>}
                <option value="skip">수단 비워두기</option>
                {paymentMethods(catalog, g.kind, true).map((p) => (
                  <option key={p.id} value={`pm:${p.id}`}>
                    기존 수단에 연결: {p.name}
                    {p.active ? '' : ' (삭제됨)'}
                  </option>
                ))}
              </select>
            </GroupDecision>
          ))}

          {preview.invalid.length > 0 && (
            <details className="catalog-item">
              <summary>
                <b>가져오지 않는 행 {preview.invalid.length}건</b>
                <span>거래일·금액이 잘못된 행은 임의로 고치지 않습니다</span>
              </summary>
              <ul className="invalid-list">
                {preview.invalid.map((i) => (
                  <li key={`${i.index}-${i.reason}`}>
                    {i.index + 1}번째 ({i.sourceId ?? 'ID 없음'}): {i.reason}
                  </li>
                ))}
              </ul>
            </details>
          )}

          {s.unresolvedCount > 0 && <p className="notice warn">결정이 필요한 항목이 {s.unresolvedCount}개 있습니다. 선택한 뒤 ‘다시 미리보기’를 눌러주세요.</p>}
          {stale && <p className="notice warn">바뀐 내용이 있습니다. ‘다시 미리보기’로 합계를 다시 확인해야 확정할 수 있습니다.</p>}
          <button className="primary save" disabled={busy || stale || s.unresolvedCount > 0 || s.transactionCount + s.recurringCount === 0} onClick={() => void confirm()}>
            위 합계대로 확정하기
          </button>
          <p className="hint">미리보기는 1시간 동안 유효합니다. 확정은 한 번에 처리되어 중간에 실패하면 아무것도 저장되지 않습니다.</p>
        </div>
      )}
    </article>
  );
}

function GroupDecision({ title, subtitle, children }: { title: string; subtitle: string; children: React.ReactNode }) {
  return (
    <div className="decision">
      <div>
        <b>{title}</b>
        <small>{subtitle}</small>
      </div>
      <div className="decision-controls">{children}</div>
    </div>
  );
}
